import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
// Enables the localized format tokens (L / LL / ll / LT …). Without it,
// `.format('ll')` returns the literal string "ll".
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { getLocale } from '../stores/useLocaleStore';
import { getLocaleMeta } from 'i18n/locales';
import importDateFormats from './importDateFormats.json';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(localizedFormat);

/**
 * Named display formats. Call sites use these instead of raw dayjs tokens so
 * that "what a date looks like" is decided in one place and is locale-aware.
 *
 *   SHORT       L        31/12/2026   · 31/12/2026   · 31/12/2026
 *   MEDIUM      ll       31 Dec 2026  · 31 dic 2026  · 31 dic 2026
 *   LONG        LL       31 December 2026 · 31 de diciembre de 2026 · 31 dicembre 2026
 *   DATETIME    ll LT    31 Dec 2026 14:30
 *   MONTH_YEAR  MMM YYYY dayjs has no localized token for this, but MMM is
 *                        itself locale-driven, so the pattern is safe.
 *   YEAR        YYYY
 *
 * NOT included, deliberately: 'YYYY-MM-DD'. That is a MACHINE format — API
 * payloads, query parameters, CSV cells, filenames — and must stay identical
 * in every language. Keep passing it literally at those call sites so the
 * distinction is visible in the diff.
 */
export const DATE_FORMAT = Object.freeze({
  SHORT: 'L',
  MEDIUM: 'll',
  LONG: 'LL',
  DATETIME: 'll LT',
  MONTH_YEAR: 'MMM YYYY',
  YEAR: 'YYYY',
});

/**
 * A dayjs instance bound to the active UI language.
 *
 * Per-instance `.locale()`, never the global `dayjs.locale()`. The global is a
 * process-wide mutation: setting it inside a PDF generator rendering an
 * Italian document for a supplier would flip every date in the live Spanish UI
 * behind it. Phase 7 depends on this staying per-instance.
 */
export const localized = (value, localeCode) =>
  dayjs(value).locale(getLocaleMeta(localeCode ?? getLocale()).dayjsLocale);

// The date spellings an import accepts, in the order tried. Not written here:
// the backend owns the list and this is its mirror (`importDateFormats.json`,
// held by `importDateFormats.drift.test.js`), so a date the browser's Excel
// parsing accepts is one every server reader accepts too — the UK sources,
// which parse their files on the server, included (backend
// docs/features/hmrc-xlsx-upload D12).
//
// Why the list has the forms it has:
//   - Two-digit years for every separator (`DD/MM/YY`, `DD-MM-YY`). Only
//     `DD.MM.YY` existed, so `1/1/24` — what an Excel cell formatted `d/m/yy`
//     hands over — was rejected outright.
//   - The forms the app itself PRINTS (`DD MMM YYYY`, `D MMM YYYY`,
//     `MMM DD, YYYY`). `formatDateUTC(value, 'DD MMM YYYY')` is how every grid
//     renders a plain date, so `11 Jan 2027` is the spelling a user sees one row
//     above the cell they are typing into. Whatever we display has to be
//     something we accept back.
//   - Every numeric form is day-first, and a month-first one is never added, so
//     no addition can change which day an accepted date means.
const SUPPORTED_FORMATS = importDateFormats.formats;

// Human-readable version of the list above (dayjs escape brackets stripped),
// for the one place a user may genuinely want the whole list — a field's help
// popover. NOT for an inline validation message: twenty formats in a table
// cell is a wall nobody reads, and it pushed a grid row to nine lines tall.
export const SUPPORTED_DATE_FORMATS_TEXT = SUPPORTED_FORMATS.map((f) =>
  f.replace(/[[\]]/g, ''),
).join(', ');

/**
 * The two or three spellings worth naming when a date is rejected.
 *
 * A person who typed a date wrong needs one example they can copy, not an
 * exhaustive grammar. The rest stays available in the column's help popover.
 */
export const DATE_FORMAT_EXAMPLES = '31/01/2027, 2027-01-31 or 31 Jan 2027';

/**
 * A parsed date as the wire wants it: a plain ISO calendar day.
 *
 * This is what makes accepting the printed forms above safe. The backend's
 * reader coerces the FILE (`"coerce": "date"` in the source spec); until now
 * nothing coerced a value a user typed into the grid, so a hand-edit went to
 * the server in whatever shape it was typed while a parsed row went as ISO.
 *
 * @returns {string} ISO `YYYY-MM-DD`, or the input unchanged if unparseable —
 *   an unparseable value is the validator's business to report, not this
 *   function's to discard.
 */
export function toIsoDate(value) {
  const parsed = parseDate(value);
  return parsed ? parsed.format('YYYY-MM-DD') : value;
}

/**
 * Parse a date string using supported formats via dayjs (strict mode).
 * Returns a dayjs object or null if the string doesn't match any format.
 *
 * @param {string} dateStr
 * @returns {import('dayjs').Dayjs | null}
 */
/**
 * Zero-pad the day and month of a purely numeric date, so one canonical format
 * covers every way a person or a spreadsheet writes the same day.
 *
 * dayjs's strict mode is stricter than it looks: `D` matches `31` but REJECTS
 * `01`, and `DD` does the opposite. Covering both means every numeric format
 * doubles — and then doubles again for a two-digit year, and again for mixed
 * padding like `01/1/24`. That is twelve near-identical entries per separator
 * in a list which is also rendered to users verbatim
 * (`SUPPORTED_DATE_FORMATS_TEXT`).
 *
 * One rule instead. It only ever touches a string that is entirely digits and
 * one repeated separator, and it only ever ADDS a leading zero, so it cannot
 * change which day a value means — `1/1/24` and `01/01/24` are the same date
 * in any reading. ISO input is left alone: it is matched before this runs, and
 * a four-digit leading component is not a day.
 *
 * A trailing `H:mm[:ss]` time gets the same treatment for its hour: HMRC's
 * Import Item Report writes `5/9/2026 6:00` beside `20/09/2026 14:46:35` in
 * one column. The backend's `padNumericDate` (utils/dates/importDateFormats.ts),
 * rule for rule.
 */
function padNumericDate(str) {
  const m = /^(\d{1,2})([/.-])(\d{1,2})\2(\d{2}|\d{4})(?: (\d{1,2}):(\d{2})(:\d{2})?)?$/.exec(str);
  if (!m) return null;
  const [, day, sep, month, year, hour, minute, seconds = ''] = m;
  const pad = (part) => part.padStart(2, '0');
  const time = hour === undefined ? '' : ` ${pad(hour)}:${minute}${seconds}`;
  const padded = `${pad(day)}${sep}${pad(month)}${sep}${year}${time}`;
  return padded === str ? null : padded;
}

// What each spelling parsed to. A column of 50,000 dates holds a few hundred
// distinct ones, and a strict parse tries up to 29 formats, each rebuilding
// its parser: uncached, date parsing was most of a 50K-row review step
// (CG-2576). Bounded, so an unbounded stream of distinct values cannot grow
// it without limit. A dayjs object is immutable, so sharing one is safe.
const PARSED = new Map();
const PARSED_LIMIT = 20_000;

export function parseDate(dateStr) {
  if (!dateStr) return null;

  const str = String(dateStr).trim();
  if (PARSED.has(str)) return PARSED.get(str);
  const parsed = parseUncached(str);
  if (PARSED.size >= PARSED_LIMIT) PARSED.clear();
  PARSED.set(str, parsed);
  return parsed;
}

function parseUncached(str) {
  for (const fmt of SUPPORTED_FORMATS) {
    const d = dayjs(str, fmt, true);
    if (d.isValid()) return d;
  }

  // Second pass, padded. Deliberately AFTER the exact ones, so a format that
  // already matches is never re-read through a rewritten string.
  const padded = padNumericDate(str);
  if (padded) {
    for (const fmt of SUPPORTED_FORMATS) {
      const d = dayjs(padded, fmt, true);
      if (d.isValid()) return d;
    }
  }

  return null;
}

/**
 * Format a calendar date (no real time component) in UTC, so the day shown
 * matches the day stored on the backend regardless of the viewer's timezone.
 *
 * Use for plain-date fields: deliveryDate, invoiceDate, saleDate, reporting
 * periods, emission record dates, etc. Do NOT use for real timestamps like
 * createdAt/updatedAt where local-time display is desired.
 *
 * The UTC pin and the locale are independent and both matter: UTC decides
 * WHICH day is shown, the locale decides how that day is spelled.
 *
 * @param {string | Date | import('dayjs').Dayjs | null | undefined} value
 * @param {string} [fmt] a DATE_FORMAT value, or a raw dayjs pattern
 * @param {string} [localeCode] pin the language (phase-7 artifacts); defaults
 *                              to the active UI language
 * @returns {string}
 */
export function formatDateUTC(value, fmt = DATE_FORMAT.MEDIUM, localeCode) {
  if (!value) return '';
  // Per-call .locale(), never the global dayjs.locale() — see `localized`.
  const d = dayjs.utc(value).locale(getLocaleMeta(localeCode ?? getLocale()).dayjsLocale);
  return d.isValid() ? d.format(fmt) : '';
}

/**
 * Format a real timestamp (createdAt, updatedAt, upload times) in the viewer's
 * local timezone and language. The counterpart to formatDateUTC: use this when
 * the moment matters, that one when the calendar day matters.
 */
export function formatDateTime(value, fmt = DATE_FORMAT.DATETIME, localeCode) {
  if (!value) return '';
  const d = localized(value, localeCode);
  return d.isValid() ? d.format(fmt) : '';
}
